import React, { useCallback, useState, useRef } from "react";
import { BsThreeDots } from "react-icons/bs";
import dayjs from "dayjs";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import media from "styled-media-query";
import styled from "styled-components";
import { NotificationTemplateEntity } from "src/firebase/db/NotificationTemplates";

import { formatDate } from "src/helpers/UtilityHelper";
import StyleConfig from "src/constants/StyleConfig";

import CheckBox from "src/components/atoms/form/CheckBox";
import Button from "src/components/atoms/button/Button";
import Tooltip from "src/components/atoms/tooltip/Tooltip";
dayjs.extend(isSameOrBefore);

type Props = {
  notificationTemplate: NotificationTemplateEntity;
  isChecked: boolean;
  isExpiredTime: boolean;
  onConfirm: (notificationTemplateId: string) => void;
  onDelete: (notificationTemplateId: string) => void;
  onEdit: (notificationTemplateId: string) => void;
  onCheck: (notificationTemplateId: string) => void;
};

const NotificationTemplateListItem = (props: Props) => {
  const [isMenuOpened, setIsMenuOpened] = useState<boolean>(false);

  const menuRef = useRef<HTMLDivElement>(null);

  const handleClickTrigger = () => {
    setIsMenuOpened(true);
    document.addEventListener("click", documentClickHandler);
  };
  const documentClickHandler = (e: any) => {
    if (menuRef.current === null) return;
    if (menuRef.current.contains(e.currentTarget)) return;
    setIsMenuOpened(false);
    document.removeEventListener("click", documentClickHandler);
  };

  const {
    notificationTemplate: {
      id,
      name,
      scheduled_time,
      can_order_time,
      last_pickup_time,
    },
    isChecked,
    isExpiredTime,
    onConfirm,
    onDelete,
    onEdit,
    onCheck,
  } = props;

  const wrapOnConfirm = useCallback(() => {
    onConfirm(id);
  }, [id, onConfirm]);

  const wrapOnDelete = useCallback(() => {
    setIsMenuOpened(false);
    onDelete(id);
  }, [id, onDelete, setIsMenuOpened]);

  const wrapOnEdit = useCallback(() => {
    onEdit(id);
  }, [id, onEdit]);

  const wrapOnCheck = useCallback(() => {
    onCheck(id);
  }, [id, onCheck]);

  return (
    <ListItem>
      <div className="notificationTemplate">
        <div
          className="notificationTemplate-menu"
          onClick={() => !isMenuOpened && handleClickTrigger()}
        >
          <BsThreeDots size={28} />
          <div ref={menuRef} className="notificationTemplate-menu-list">
            {isMenuOpened && (
              <div className="notificationTemplate-menu-list-items">
                <div
                  className="notificationTemplate-menu-list-items-edit"
                  onClick={wrapOnEdit}
                >
                  テンプレートを編集する
                </div>
                <div
                  className="notificationTemplate-menu-list-items-delete"
                  onClick={wrapOnDelete}
                >
                  テンプレートを削除する
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="notificationTemplate-description">
          <div
            className="notificationTemplate-description-title"
            data-tip={
              isExpiredTime
                ? "設定している時刻が現在時刻より早いため、選択できません"
                : null
            }
          >
            <CheckBox
              isDisabled={isExpiredTime}
              children={"お知らせテンプレート名"}
              isChecked={isChecked}
              onChange={wrapOnCheck}
            />
            <Tooltip type="error" />
          </div>
          <p className="notificationTemplate-description-name">{name}</p>
          <div className="notificationTemplate-description-timestamps">
            <p>
              <span>お知らせ時刻</span>
              <span>{formatDate(scheduled_time, "HH:mm")}</span>
            </p>
            <p>
              <span>ラストオーダー時刻</span>
              <span>{formatDate(can_order_time, "HH:mm")}</span>
            </p>
            <p>
              <span>最終受取時刻</span>
              <span>{formatDate(last_pickup_time, "HH:mm")}</span>
            </p>
          </div>
        </div>
        <div className="notificationTemplate-create">
          <Button
            isDisabled={false}
            clickEvent={() => wrapOnConfirm()}
            text={`通知を配信する`}
          />
        </div>
      </div>
    </ListItem>
  );
};

const ListItem = styled.div`
  background-color: #fff;
  color: ${StyleConfig.COLOR.TEXT_COLOR};
  margin-bottom: ${StyleConfig.HELPER.M16};
  padding: 20px;
  font-size: 16px;
  font-weight: bold;
  line-height: ${20 / 16};

  .notificationTemplate {
    position: relative;
    display: inline-flex;
    align-items: flex-end;
    width: 100%;
    flex-wrap: wrap;

    input[type="checkbox"] {
      border: 1px solid ${StyleConfig.COLOR.INPUT_BORDER} !important;
      &:disabled {
        background-color: ${StyleConfig.COLOR.INPUT_BORDER};
        cursor: not-allowed;
      }
    }

    .notificationTemplate-menu {
      position: absolute;
      top: -14px;
      right: 0;
      z-index: 5;
      cursor: pointer;

      .notificationTemplate-menu-list {
        position: relative;
        background: #fff;

        .notificationTemplate-menu-list-items {
          position: absolute;
          top: 0;
          right: 0;
          width: 250px;
          text-align: center;
          border-radius: 5px;
          border: 1px solid ${StyleConfig.COLOR.INPUT_BORDER};
          background: #fff;
          z-index: 5;

          > * {
            font-weight: normal;
            padding: 15px 20px;
            cursor: pointer;

            &:first-child {
              border-bottom: 1px solid ${StyleConfig.COLOR.INPUT_BORDER};
            }

            &.notificationTemplate-menu-list-items-delete {
              color: ${StyleConfig.COLOR.SECONDORY};
            }
          }
        }
      }
    }

    .notificationTemplate-description {
      flex: 100%;

      .notificationTemplate-description-title {
        text-align: center;
        font-size: ${StyleConfig.TYPO.SMLTEXT};
        margin-bottom: ${StyleConfig.HELPER.M8};
        ${media.greaterThan("medium")`
          text-align: left;
        `};

        span {
          font-weight: normal;
        }
      }

      .notificationTemplate-description-name {
        text-align: center;
        font-size: ${StyleConfig.TYPO.LV2};
        font-weight: bold;
        margin-bottom: ${StyleConfig.HELPER.M16};
        ${media.greaterThan("medium")`
          text-align: left;
        `};
      }

      .notificationTemplate-description-timestamps {
        ${media.greaterThan("medium")`
          display: inline-grid;
          width: 40%;
        `};
        p {
          font-size: ${StyleConfig.TYPO.SMLTEXT};
          display: inline-flex;
          width: 100%;
          text-align: right;
          font-weight: 300;

          ${media.greaterThan("medium")`
            text-align: left;
            &:not(:last-child) {
              margin-bottom: 4px;
            }
          `};

          span {
            flex: 1;
            :first-child {
              color: ${StyleConfig.COLORS.GRAY6};
            }
            :last-child {
              max-width: 80px;
              min-width: 80px;
              ${media.greaterThan("medium")`
                max-width: 100px;
                min-width: 100px:
              `}
            }
          }
        }
      }
    }

    .notificationTemplate-create {
      flex: 100%;
      display: inline-flex;
      justify-content: flex-end;
      margin-top: 28px;
      > * {
        display: inline;
      }

      ${media.greaterThan("medium")`
        margin-top: 20px;
        flex: 50%;
      `};
    }
  }
`;

export default NotificationTemplateListItem;
